import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { locationService } from '@website/services/location.service';

interface GeolocationPosition {
  latitude: number;
  longitude: number;
}

interface IpLocationData {
  latitude: number;
  longitude: number;
  city?: string;
  name?: string;
}

type GeolocationPermissionState = 'prompt' | 'granted' | 'denied' | 'unavailable';

/**
 * Shared permission state - singleton across all hook instances
 * This ensures we only check permission once and all components share the result
 */
let cachedPermissionState: GeolocationPermissionState | null = null;
let permissionCheckPromise: Promise<GeolocationPermissionState> | null = null;

async function getPermissionState(): Promise<GeolocationPermissionState> {
  // Return cached state if available
  if (cachedPermissionState) {
    return cachedPermissionState;
  }

  // Return existing promise if check is in progress
  if (permissionCheckPromise) {
    return permissionCheckPromise;
  }

  // Create new permission check promise
  permissionCheckPromise = (async () => {
    if (typeof window === 'undefined') {
      cachedPermissionState = 'unavailable';
      return 'unavailable';
    }

    // Check if geolocation is available
    if (!('geolocation' in navigator)) {
      cachedPermissionState = 'unavailable';
      return 'unavailable';
    }

    // Use Permissions API to check current permission state (if available)
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        cachedPermissionState = result.state as GeolocationPermissionState;

        // Monitor permission changes
        result.onchange = () => {
          cachedPermissionState = result.state as GeolocationPermissionState;
          console.log('[LOCATION] Permission state changed to:', result.state);
        };

        return cachedPermissionState;
      } catch {
        cachedPermissionState = 'prompt';
        return 'prompt';
      }
    }

    cachedPermissionState = 'prompt';
    return 'prompt';
  })();

  return permissionCheckPromise;
}

/**
 * Get browser geolocation using Promise-based API
 */
async function getBrowserLocation(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      reject(new Error('Geolocation not available'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
}

/**
 * React Query-based permission check - truly shared across all components
 * Only runs once and all components get the same result from cache
 */
function useGeolocationPermission() {
  const permissionQuery = useQuery({
    queryKey: ['geolocation', 'permission'],
    queryFn: async () => {
      const state = await getPermissionState();
      return state;
    },
    staleTime: Infinity, // Permission state doesn't change often
    refetchOnWindowFocus: false,
    retry: 0
  });

  // Poll for permission changes in background (detects changes in other tabs/windows)
  useEffect(() => {
    const interval = setInterval(() => {
      if (cachedPermissionState && cachedPermissionState !== permissionQuery.data) {
        // Invalidate query to trigger refetch
        permissionQuery.refetch();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [permissionQuery]);

  return {
    permissionState: permissionQuery.data || 'prompt',
    permissionChecked: permissionQuery.isFetched
  };
}

/**
 * Hook to get the user's current position with optimal UX:
 *
 * Strategy:
 * 1. Check geolocation permission state (shared across all components via React Query)
 * 2. If permission is ALREADY granted → use browser geolocation (more precise)
 * 3. If permission is NOT granted → use IP location (don't prompt user)
 * 4. Permission can be requested explicitly via user interactions (e.g., location picker)
 * 5. If both fail → return undefined (components show fallback like popular activities)
 *
 * Priority: browser location (if granted) > IP location > undefined
 *
 * Note: This hook uses React Query for ALL state sharing - permission check, browser location,
 * and IP location are all shared across components, preventing duplicate API calls.
 */
export const useCurrentPosition = () => {
  // Use React Query for permission check - shared across all components
  const { permissionState, permissionChecked } = useGeolocationPermission();

  // Browser geolocation query - ONLY if permission is granted
  // React Query ensures this is only fetched once and shared across all components
  const browserLocationQuery = useQuery({
    queryKey: ['geolocation', 'browser'],
    queryFn: async (): Promise<GeolocationPosition | null> => {
      try {
        const location = await getBrowserLocation();
        console.log('[LOCATION] Using browser geolocation:', location);
        return location;
      } catch {
        return null;
      }
    },
    enabled: permissionChecked && permissionState === 'granted',
    refetchOnWindowFocus: false,
    staleTime: Infinity, // Cache indefinitely - location doesn't change often
    retry: 1
  });

  // IP-based location query - ONLY if browser location unavailable
  // React Query ensures this is only fetched once and shared across all components
  const ipLocationQuery = useQuery({
    queryKey: ['geolocation', 'ipLocation'],
    queryFn: async (): Promise<IpLocationData | null> => {
      try {
        const ipLocation = await locationService.getLocationByIP();
        if (ipLocation) {
          console.log('[LOCATION] Using IP-based location:', {
            latitude: ipLocation.latitude,
            longitude: ipLocation.longitude,
            city: ipLocation.city
          });
          return {
            latitude: ipLocation.latitude,
            longitude: ipLocation.longitude,
            city: ipLocation.city,
            name: ipLocation.name
          };
        }
      } catch {
        return null;
      }
      return null;
    },
    enabled: permissionChecked && permissionState !== 'granted',
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: 1
  });

  // Determine the final position to use
  // Priority: browser location (more precise) > IP location (fallback) > undefined
  const position = browserLocationQuery.data || ipLocationQuery.data || undefined;

  // Calculate loading state
  const isLoading = browserLocationQuery.isLoading || ipLocationQuery.isLoading;

  // Error only if both methods failed
  const isError =
    (browserLocationQuery.isError || !browserLocationQuery.data) &&
    (ipLocationQuery.isError || !ipLocationQuery.data) &&
    permissionState !== 'prompt';

  return {
    data: position,
    isLoading,
    isError,
    error: browserLocationQuery.error || ipLocationQuery.error,
    hasBrowserLocation: !!browserLocationQuery.data
  };
};
